import type { Health } from './types'
import type { ComponentProps } from 'react'
import type { Badge } from '@cloudflare/kumo'

type BadgeVariant = ComponentProps<typeof Badge>['variant']

export interface HealthStyle {
  label: string
  /** Kumo Badge variant for this health state. */
  badge: BadgeVariant
  /** Whether the Badge should render as a dot (only success/warning/error/neutral). */
  dot: boolean
  /** Tailwind classes (Kumo tokens) for a solid matrix-cell fill. */
  cell: string
  /** Tailwind text-color class (Kumo token). */
  text: string
  /** Tailwind bg class (Kumo token) for a status dot. */
  dotBg: string
}

/**
 * Central mapping of health → Kumo tokens + label. Used everywhere for
 * consistency. `blocked` is the most severe state; it reuses the danger color
 * but renders filled (not a dot) so it visually outranks a plain error.
 */
export const HEALTH_STYLES: Record<Health, HealthStyle> = {
  ok: {
    label: 'OK',
    badge: 'success',
    dot: true,
    cell: 'bg-kumo-success/55',
    text: 'text-kumo-success',
    dotBg: 'bg-kumo-success',
  },
  degraded: {
    label: 'Degraded',
    badge: 'warning',
    dot: true,
    cell: 'bg-kumo-warning',
    text: 'text-kumo-warning',
    dotBg: 'bg-kumo-warning',
  },
  error: {
    label: 'Error',
    badge: 'error',
    dot: true,
    cell: 'bg-kumo-danger/80',
    text: 'text-kumo-danger',
    dotBg: 'bg-kumo-danger',
  },
  blocked: {
    label: 'Blocked',
    badge: 'error',
    dot: false,
    cell: 'bg-kumo-danger ring-2 ring-kumo-danger ring-offset-1 ring-offset-kumo-base',
    text: 'text-kumo-danger',
    dotBg: 'bg-kumo-danger',
  },
  no_data: {
    label: 'No data',
    badge: 'neutral',
    dot: true,
    cell: 'bg-transparent',
    text: 'text-kumo-subtle',
    dotBg: 'bg-kumo-fill',
  },
}

export function healthStyle(h: Health): HealthStyle {
  return HEALTH_STYLES[h] ?? HEALTH_STYLES.no_data
}

/** Short relative-time formatter for "last seen" fields. */
export function relativeTime(iso: string | null): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const diff = Date.now() - then
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

export function fmtPct(v: number | null): string {
  return v === null ? '—' : `${v.toFixed(1)}%`
}

export function fmtNum(v: number | null): string {
  return v === null ? '—' : v.toLocaleString()
}
